<script lang="ts">
  /**
   * The house minds itself while you are away — F16, on the trip object page.
   *
   * The design brief for this screen is one sentence: **never let it say the house
   * is protected when it is not.** Everything else follows from that.
   *
   * Three asks, and they do not have the same amount of reality behind them. The
   * house-sitter's pass has a real path — Fabric owns identity, and Waypoint asks
   * it over its API with its own service account. The irrigation and the anomaly
   * thresholds live in Home Assistant, and this service has no path to Home
   * Assistant at all. So each ask carries its own state, its own glyph, its own
   * word, and — when it is not through — the exact thing that would connect it.
   *
   * Two rules from `07-acceptance.md` are doing most of the work here:
   *
   *  · **Never colour alone.** Every state on this screen is a glyph AND a word
   *    AND a colour. Greyscale it and nothing becomes ambiguous.
   *  · **Blue `--ok` for good, amber `--warn` for attention, and no green
   *    anywhere.** "Armed" is blue. A green "armed" would be the single most
   *    dangerous pixel in the estate: green reads as *safe, stop looking*, and
   *    this is the screen somebody glances at on the way to the car.
   *
   * The word `armed` is never produced in this file from anything the browser
   * knows. It arrives from the server, which only writes it from the far side's
   * own answer. See `server/src/away.ts`.
   */
  import { Icon, Panel, dateZA } from "@steyncd/steyn-design";
  import { api, ApiError } from "../lib/api";
  import { loadCached, net } from "../lib/offline.svelte";
  import type { AwayProfile, Ask, AskState, AwayState } from "../lib/trips";

  let { tripId, onchanged }: { tripId: string; onchanged?: () => void } = $props();

  let away = $state<AwayProfile | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);
  /** Null when live; the time of the copy when it is not. */
  let cachedAt = $state<string | null>(null);
  let working = $state(false);
  let actionErr = $state<string | null>(null);

  async function load() {
    loading = true;
    const r = await loadCached<AwayProfile>(`/trips/${tripId}/away`);
    away = r.data;
    error = r.error;
    cachedAt = r.fresh ? null : r.at;
    loading = false;
  }

  $effect(() => {
    void load();
  });

  async function act(path: "arm" | "stand-down") {
    working = true;
    actionErr = null;
    try {
      away = await api.post<AwayProfile>(`/trips/${tripId}/away/${path}`);
      cachedAt = null;
      // The history on the page above gains two or three lines from this, and a
      // stale history under a fresh profile reads as though nothing was recorded.
      onchanged?.();
    } catch (e) {
      actionErr =
        e instanceof ApiError
          ? e.message
          : "Could not reach Waypoint. Arming the house needs signal — nothing was changed.";
    } finally {
      working = false;
    }
  }

  /**
   * Glyph, word and tone for the profile as a whole.
   *
   * `declared` is the state this component exists for: the profile was armed, the
   * record is real, and nothing in the house moved. It is amber and it says so in
   * words, because the alternative — a quiet grey "saved" — is precisely the lie.
   */
  const CHIP: Record<AwayState, { glyph: string; word: string; tone: "ok" | "warn" | "idle" }> = {
    "no-dates": { glyph: "pause", word: "No dates yet", tone: "idle" },
    "not-armed": { glyph: "pause", word: "Not armed", tone: "idle" },
    declared: { glyph: "alert", word: "Recorded, not armed", tone: "warn" },
    partly: { glyph: "alert", word: "Partly armed", tone: "warn" },
    armed: { glyph: "check", word: "Armed", tone: "ok" },
  };

  /** The same three signals, per ask. */
  const ASK_CHIP: Record<AskState, { glyph: string; word: string; tone: "ok" | "warn" }> = {
    armed: { glyph: "check", word: "Armed", tone: "ok" },
    refused: { glyph: "alert", word: "Refused", tone: "warn" },
    unreachable: { glyph: "alert", word: "No answer", tone: "warn" },
    "not-connected": { glyph: "alert", word: "Not connected", tone: "warn" },
  };

  /** A glyph per ask, so the three rows are distinguishable at a glance. */
  const ASK_GLYPH: Record<string, string> = {
    "sitter-pass": "users",
    irrigation: "droplet",
    thresholds: "shield",
  };

  const chip = $derived(CHIP[away?.state ?? "not-armed"]);
  const asks = $derived<Ask[]>(away?.asks ?? []);

  /**
   * The count, and the one place an em dash matters more than a number.
   *
   * Before anything is armed nothing has been asked, so there is no figure — a
   * "0 of 3" there would be a measurement of a thing that never happened. Once it
   * IS armed, `0 of 3` is a real and deliberately loud reading.
   */
  const tally = $derived.by(() => {
    if (!away || away.armedAt === null || away.standDownAt !== null) {
      return { value: "—", label: "nothing asked for yet" };
    }
    return { value: `${away.through} of ${away.total}`, label: "actually went through" };
  });
</script>

<section class="card sd-panel">
  <div class="head">
    <span class="kicker"><Icon name="house" size={13} /> While you are away</span>
    <span class="chip chip--{chip.tone}">
      <Icon name={chip.glyph} size={12} />
      {chip.word}
    </span>
  </div>

  {#if cachedAt}
    <!-- An arming state read from a cache is the worst possible thing to present
         as live: it is a claim about the house, made from a phone in a car. -->
    <p class="note note--quiet">
      <Icon name="pause" size={12} />
      <span
        ><strong>No signal.</strong> This is the profile as it was on {dateZA(cachedAt)}, saved on this device. It may
        not be what the house is doing now, and nothing here can be armed without signal.</span
      >
    </p>
  {/if}

  <Panel {loading} {error} isEmpty={!loading && !error && asks.length === 0} onretry={load} rows={3}>
    {#snippet empty()}
      There is no away profile for this trip yet. Give it a departure and a return date, move it to
      <em>planned</em>, and this becomes the one place that says what the house will and will not do while nobody is
      here.
    {/snippet}

    <p class="sentence">{away?.headline}</p>

    <div class="tally">
      <span class="tally__v num">{tally.value}</span>
      <span class="tally__u">{tally.label}</span>
    </div>

    {#each asks as a (a.id)}
      {@const s = ASK_CHIP[a.state]}
      <div class="ask" class:ask--warn={s.tone === "warn"}>
        <span class="ask__g" aria-hidden="true"><Icon name={ASK_GLYPH[a.id] ?? "info"} size={15} /></span>
        <div class="grow">
          <div class="ask__t">{a.label}</div>
          <div class="ask__wants">{a.wants}</div>
          <div class="ask__why">{a.why}</div>
          {#if a.needs}
            <!-- The whole value of an unarmed row. "Not connected" with no named
                 remedy is a shrug; with one it is a task. -->
            <div class="ask__needs">
              <span class="ask__needs-k">What would connect it</span>
              {a.needs}
            </div>
          {/if}
        </div>
        <span class="chip chip--{s.tone}">
          <Icon name={s.glyph} size={12} />
          {s.word}
        </span>
      </div>
    {/each}

    {#if away?.passExpiresAt}
      <p class="note">
        <Icon name="clock" size={12} />
        <span>
          <strong>The pass would close</strong> at 18:00 on {dateZA(away.passExpiresAt)} — the day you are back. A
          Fabric pass carries an expiry and no start, so one asked for today is live from today, not from the day you
          leave.
        </span>
      </p>
    {/if}

    {#if away?.armedAt}
      <p class="meta">
        Armed by {away.armedBy ?? "somebody"} on {dateZA(away.armedAt)}{#if away.standDownAt}, stood down on {dateZA(
            away.standDownAt,
          )}{/if}.
      </p>
    {/if}

    {#if away?.blockedBecause}
      <p class="note">
        <Icon name="alert" size={12} />
        <span><strong>Cannot be armed.</strong> {away.blockedBecause}</span>
      </p>
    {/if}

    {#if actionErr}
      <p class="err"><Icon name="alert" size={12} /> {actionErr}</p>
    {/if}

    <div class="acts">
      {#if away && away.armedAt !== null && away.standDownAt === null}
        <button class="lnk" onclick={() => act("stand-down")} disabled={working || !net.online}>
          {working ? "Standing down…" : "Stand it down"}
        </button>
        <span class="sub">
          Clears Waypoint's claim. A pass that did issue is not revoked from here — only an owner can do that, from
          Fabric.
        </span>
      {:else}
        <button class="btn-primary" onclick={() => act("arm")} disabled={working || !net.online || !away?.canArm}>
          {working ? "Asking…" : "Arm the house"}
        </button>
        <span class="sub">
          {#if !net.online}
            Arming needs signal. Everything already on this page can be read without it.
          {:else if away?.canArm}
            Asks for what it can, records what it cannot, and never reports more than it got.
          {:else}
            {away?.blockedBecause ?? "Nothing to arm yet."}
          {/if}
        </span>
      {/if}
    </div>
  </Panel>
</section>

<style>
  .card {
    padding: var(--sp-4) var(--pad-x) var(--sp-5);
    border: 1px solid var(--line);
    border-radius: var(--r-surface);
    background: var(--s1);
  }
  .head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--sp-3);
    margin-bottom: var(--sp-3);
  }
  .kicker { display: inline-flex; align-items: center; gap: 6px; }
  .sub { font-size: var(--fs-small); color: var(--mut); }
  .grow { flex: 1; min-width: 0; }
  .num { font-variant-numeric: tabular-nums; }

  .sentence { margin: 0 0 var(--sp-3); font-size: var(--fs-body); color: var(--tx2); }

  /* Glyph AND word AND colour — the chip is never the colour on its own, and the
     border is what carries it in a greyscale screenshot. */
  .chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 9px;
    border-radius: var(--r-pill);
    border: 1px solid var(--line);
    font-size: var(--fs-micro);
    font-weight: 700;
    color: var(--mut);
    white-space: nowrap;
    flex-shrink: 0;
  }
  .chip--ok { color: var(--ok); border-color: var(--ok); }
  .chip--warn { color: var(--warn); border-color: var(--warn); }
  .chip--idle { color: var(--mut); }

  /* One figure, in the home surface's serif, with its unit demoted beside it. */
  .tally {
    display: flex;
    align-items: baseline;
    gap: var(--sp-2);
    margin-bottom: var(--sp-3);
  }
  .tally__v { font-family: var(--font-figure); font-size: var(--fs-h2); font-weight: 700; color: var(--tx); }
  .tally__u { font-size: var(--fs-micro); font-weight: 700; color: var(--mut); }

  .ask {
    display: flex;
    align-items: flex-start;
    gap: var(--sp-3);
    min-height: var(--row-h);
    padding: var(--sp-3) 0;
    border-bottom: 1px solid var(--line);
  }
  .ask:last-of-type { border-bottom: none; }
  /* A left rule as a fourth signal on anything that did not land. */
  .ask--warn { padding-left: var(--sp-3); border-left: 2px solid var(--warn); }
  .ask__g { color: var(--mut); padding-top: 2px; flex-shrink: 0; }
  .ask__t { font-size: var(--fs-body); font-weight: 700; color: var(--tx); }
  .ask__wants { margin-top: 2px; font-size: var(--fs-small); color: var(--tx2); }
  .ask__why { margin-top: 4px; font-size: var(--fs-micro); color: var(--mut); }
  .ask__needs {
    margin-top: 6px;
    padding: var(--sp-2) var(--sp-3);
    border-radius: var(--r-control);
    background: var(--fill);
    font-size: var(--fs-micro);
    color: var(--tx2);
    line-height: 1.55;
  }
  .ask__needs-k {
    display: block;
    font-weight: 700;
    color: var(--mut);
    letter-spacing: 0.03em;
  }

  .note {
    display: flex;
    align-items: flex-start;
    gap: 7px;
    margin: var(--sp-3) 0 0;
    padding: var(--sp-2) var(--sp-3);
    border-left: 2px solid var(--warn);
    border-radius: var(--r-control);
    background: var(--fill);
    font-size: var(--fs-small);
    color: var(--tx2);
  }
  .note--quiet { border-left-color: var(--mut); margin: 0 0 var(--sp-3); }

  .meta { margin: var(--sp-3) 0 0; font-size: var(--fs-micro); color: var(--mut); }
  .err {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin: var(--sp-3) 0 0;
    font-size: var(--fs-small);
    font-weight: 700;
    color: var(--warn);
  }

  .acts {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--sp-3);
    margin-top: var(--sp-4);
    padding-top: var(--sp-3);
    border-top: 1px solid var(--line);
  }
  .lnk { display: inline-flex; align-items: center; gap: 5px; font-size: var(--fs-micro); font-weight: 700; color: var(--mut); }
  .lnk:hover { color: var(--acc); }

  /* The two columns collapse in a narrow container, never a sideways scroll. */
  @container panel (max-width: 520px) {
    .ask { flex-wrap: wrap; }
  }

  /* The print sheet is read in a car, and "the house is not armed" is exactly the
     kind of thing you want on the page you took with you. */
  @media print {
    .acts { display: none; }
    .card { border: 1px solid #999; background: #fff; box-shadow: none; padding: 8px 10px; }
    .ask__t, .sentence, .tally__v { color: #000; }
    .ask__wants, .ask__why, .ask__needs, .meta { color: #333; }
    .ask, .note { break-inside: avoid; }
  }
</style>
